import React from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { Switch } from '../../../../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../../../components/ui/select';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '../../../../components/ui/field';
import { ProposalField } from '../../../../common/hooks/use-assistant-api.hook';

/**
 * The proposal, as something to fill in rather than only agree to.
 *
 * The assistant proposing "set the description to X" used to leave a vendor
 * two moves: accept X exactly, or decline and describe the change again in
 * different words. Nearly-right was the common case and the expensive one.
 *
 * So the change arrives with its parts exposed. The fields come from the
 * server, derived from the arguments the assistant actually proposed — a
 * description change is a Description box, and asking for the price too grows
 * a Price box beside it. Nothing here knows which tool it is rendering, which
 * is what lets it serve all of them.
 *
 * Edits are held by the caller, keyed by field path, and sent with the
 * approval. Only what the vendor touched is sent: an untouched field is not an
 * edit, and re-sending its value would make every approval look like one.
 */

interface Props {
    fields: ProposalField[];
    /** What the vendor has changed so far, keyed by field path. */
    edits: Record<string, unknown>;
    onChange(path: string, value: unknown): void;
    disabled?: boolean;
}

/** What a `list` field looks like in a text box, and how it comes back. */
const joinList = (value: unknown): string =>
    Array.isArray(value) ? value.map((v) => String(v)).join(', ') : String(value ?? '');

export const ProposalForm: React.FC<Props> = ({ fields, edits, onChange, disabled }) => {
    const { t } = useTranslation();

    if (!fields.length) return null;

    /**
     * The label, translated where we know the argument and humanised where we
     * do not.
     *
     * The server derives labels from argument names, and a tool can be added
     * without anyone adding four translations for it — so an unknown key falls
     * back to the English the server worked out rather than showing a missing
     * key.
     */
    const labelFor = (field: ProposalField): string =>
        t(`ASSISTANT.FIELD.${field.key.toUpperCase()}`, { defaultValue: field.label }) as string;

    /**
     * A line under the field, only where we have one in the vendor's language.
     *
     * The server can derive a hint from the tool's own description of the
     * argument, but that text is written for a model and written in English —
     * showing it raw would put untranslated prose in front of a vendor reading
     * the rest of the panel in French. So the hint is translated or it is not
     * shown, and the label carries the meaning on its own until it is.
     */
    const hintFor = (field: ProposalField): string =>
        t(`ASSISTANT.FIELD.HINT.${field.key.toUpperCase()}`, { defaultValue: '' }) as string;

    const valueOf = (field: ProposalField): unknown =>
        Object.prototype.hasOwnProperty.call(edits, field.path) ? edits[field.path] : field.value;

    const control = (field: ProposalField) => {
        const value = valueOf(field);
        const id = `proposal-${field.path}`;

        switch (field.kind) {
            case 'textarea':
                return (
                    <Textarea
                        id={id}
                        disabled={disabled}
                        value={String(value ?? '')}
                        rows={3}
                        onChange={(e) => onChange(field.path, e.target.value)}
                    />
                );

            case 'boolean':
                return (
                    <Switch
                        id={id}
                        disabled={disabled}
                        checked={!!value}
                        onCheckedChange={(checked) => onChange(field.path, checked)}
                    />
                );

            case 'number':
                return (
                    <Input
                        id={id}
                        type="number"
                        disabled={disabled}
                        value={String(value ?? '')}
                        onChange={(e) => onChange(field.path, e.target.value)}
                    />
                );

            case 'color':
                return (
                    <div className="flex items-center gap-2">
                        {/*
                          * The swatch is the picker, and the hex stays visible
                          * beside it — a vendor matching a brand colour has the
                          * code written down somewhere and needs to type it.
                          */}
                        <input
                            aria-label={labelFor(field)}
                            type="color"
                            disabled={disabled}
                            value={String(value ?? '#000000').slice(0, 7)}
                            onChange={(e) => onChange(field.path, e.target.value)}
                            className="size-9 shrink-0 cursor-pointer border bg-background p-1"
                        />
                        <Input
                            id={id}
                            disabled={disabled}
                            value={String(value ?? '')}
                            onChange={(e) => onChange(field.path, e.target.value)}
                        />
                    </div>
                );

            case 'select':
                return (
                    <Select
                        disabled={disabled}
                        value={String(value ?? '')}
                        onValueChange={(next) => onChange(field.path, next)}
                    >
                        <SelectTrigger id={id}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                {(field.options ?? []).map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                );

            case 'list':
                return (
                    <Input
                        id={id}
                        disabled={disabled}
                        value={joinList(value)}
                        placeholder={t('ASSISTANT.FIELD.LIST_PLACEHOLDER', 'Separate with commas') as string}
                        onChange={(e) => onChange(field.path, e.target.value)}
                    />
                );

            default:
                return (
                    <Input
                        id={id}
                        disabled={disabled}
                        value={String(value ?? '')}
                        onChange={(e) => onChange(field.path, e.target.value)}
                    />
                );
        }
    };

    return (
        <FieldGroup className="mt-3 gap-3">
            {fields.map((field) => (
                <Field
                    key={field.path}
                    // A switch reads as a setting rather than a blank to fill,
                    // so the label sits beside it instead of above.
                    orientation={field.kind === 'boolean' ? 'horizontal' : 'vertical'}
                    data-disabled={disabled || undefined}
                >
                    <FieldLabel htmlFor={`proposal-${field.path}`}>{labelFor(field)}</FieldLabel>
                    {control(field)}
                    {!!hintFor(field) && <FieldDescription>{hintFor(field)}</FieldDescription>}
                </Field>
            ))}
        </FieldGroup>
    );
};
